import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DebounceService } from 'src/app/core/services/debounce.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { ApiService } from 'src/app/services/api.service';
import { AdsDashboardService } from '../ads.service';
import { PaginationHelperService } from 'src/app/services/pagination-helper.service';
import { firstValueFrom } from 'rxjs';
import { TableControlsComponent } from 'src/app/shared/component/table-controls/table-controls.component';

import { CreateEditAdsModalComponent } from '../create-edit-ads-modal/create-edit-ads-modal.component';

// CONSTANTS
import {
  ADS_TABLE_HEADERS,
  ADS_TABLE_SUB_HEADERS,
  ADS_SCREEN_TABLE_HEADERS,
  ADS_SCREEN_TABLE_SUB_HEADERS,
  AdType
} from 'src/app/lib/constants/ads.constants';

@Component({
  selector: 'app-ads-dashboard',
  templateUrl: './ads-dashboard.component.html',
  styleUrls: ['./ads-dashboard.component.scss'],
})
export class AdsDashboardComponent implements OnInit {

  @ViewChild('tableControls') tableControls!: TableControlsComponent;

  isLoading = false;
  ads_list: any;
  lastPage: any;

  // ─── Active tab (replaces the old ads-dashboard-screen / ads-dashboard-link routes) ──
  activeAdType: AdType = 'screen';


  // Table configuration switches with the active tab
  get tableHeader() {
    return this.activeAdType === 'screen' ? ADS_SCREEN_TABLE_HEADERS : ADS_TABLE_HEADERS;
  }
  get tableSubHeader() {
    return this.activeAdType === 'screen' ? ADS_SCREEN_TABLE_SUB_HEADERS : ADS_TABLE_SUB_HEADERS;
  }

  // Pagination
  size = 5;
  total_pages: any;
  current_page = 1;
  pages_array: number[] = [];
  total: any;

  // Search
  searchKey: any = null;
  searchForm!: FormGroup;
  search_keyword = '';

  // Limit
  limitKey: any = null;
  limitForm!: FormGroup;

  // Sorting
  sort_column = 'dateTime';
  sort_type: 'ASC' | 'DESC' = 'ASC';

  // Export/filter params (used for both list fetching and export)
  filterFields: any = {
    search: null,
  };
  fileName: any;

  constructor(
    private fb: FormBuilder,
    public debounce: DebounceService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private adsService: AdsDashboardService,
    private apiService: ApiService,
    public paginationHelper: PaginationHelperService,
    private dialog: MatDialog,
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.get_ads_list();
  }

  initializeForm() {
    this.searchKey = this.debounce.debounce(() => {
      this.current_page = 1;
      this.search_keyword = this.searchForm.value.search;
      this.filterFields.search = this.search_keyword ? this.search_keyword : null;
      this.get_ads_list();
    }, 500);

    this.searchForm = this.fb.group({
      search: [''],
    });

    this.limitKey = this.debounce.debounce(() => {
      this.current_page = 1;
      this.size = this.limitForm.value.limit;
      this.get_ads_list();
    }, 500);

    this.limitForm = this.fb.group({
      limit: [''],
    });
  }

  // ─── Tabs ────────────────────────────────────────────────────────────────

  setActiveTab(adType: AdType): void {
    if (this.activeAdType === adType) return;
    this.activeAdType = adType;
    this.current_page = 1;
    this.get_ads_list();
  }

  onLimitChangeFromComponent(event: any): void {
    this.limitForm.patchValue({ limit: event.target.value });
    this.limitKey();
  }

  onSearchKeyupFromComponent(event: any): void {
    this.searchForm.patchValue({ search: event.target.value });
    this.searchKey();
  }

  sortList(sort_column: string, sort_type: 'ASC' | 'DESC') {
    this.spinner.show().catch((err) => {
      console.error('Spinner show sort error:', err);
    });

    const { column, type } = this.paginationHelper.toggleSort(
      this.sort_column,
      this.sort_type,
      sort_column,
      sort_type
    );

    this.sort_column = column;
    this.sort_type = type;

    if (this.ads_list) {
      this.get_ads_list();
    }
  }

  // ─── Modal (create / edit) ───────────────────────────────────────────────

  openCreateModal(): void {
    const dialogRef = this.dialog.open(CreateEditAdsModalComponent, {
      panelClass: 'create-edit-ads-dialog',
      data: { mode: 'create', adType: this.activeAdType },
    });

    dialogRef.afterClosed().subscribe((didSave) => {
      if (didSave) {
        this.get_ads_list();
      }
    });
  }

  openEditModal(uid: number): void {
    const dialogRef = this.dialog.open(CreateEditAdsModalComponent, {
      panelClass: 'create-edit-ads-dialog',
      data: { mode: 'edit', uid },
    });

    dialogRef.afterClosed().subscribe((didSave) => {
      if (didSave) {
        this.get_ads_list();
      }
    });
  }

  // ─── List / pagination ───────────────────────────────────────────────────

  async get_ads_list() {
    this.spinner.show().catch((err) => {
      console.error('Spinner show error:', err);
    });

    this.isLoading = true;

    const params = {
      search: this.search_keyword ? this.search_keyword : null,
      adsType: this.activeAdType === 'screen' ? '2' : '1',
      sortBy: {
        [this.sort_column]: this.sort_type,
      },
    };

    this.filterFields = { ...params };
    this.pages_array = [];

    try {
      const response = await firstValueFrom(
        this.apiService.getAds(params, this.current_page, this.size)
      );

      if (response.status === 200) {
        this.ads_list = response.result.data;
        this.isLoading = false;

        this.pages_array = this.paginationHelper.generatePageArray(
          this.current_page,
          response.result.totalPages
        );

        this.total_pages = response.result.totalPages;
        this.current_page = response.result.currentPage;
        this.total = response.result.totalItems;
        this.lastPage = response.result.totalPages;

        setTimeout(() => {
          this.spinner.hide().catch((err) => {
            console.error('Spinner hide error:', err);
          });
        }, 500);
      }
    } catch (error) {
      this.isLoading = false;
      this.spinner.hide().catch((err) => {
        console.error('Spinner hide error:', err);
      });
    }
  }

  setPage(num: number) {
    this.current_page = num;
    this.get_ads_list();
  }

  nextPage() {
    if (this.current_page < Number(this.total_pages)) {
      this.current_page++;
      this.get_ads_list();
    }
  }

  prevPage() {
    if (this.current_page > 1) {
      this.current_page--;
      this.get_ads_list();
    }
  }

  keyDownFunction(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.searchList();
    }
  }

  searchList() {
    this.current_page = 1;
    this.search_keyword = this.searchForm.value.search;
    this.filterFields.search = this.search_keyword ? this.search_keyword : null;
    this.get_ads_list();
  }

  async refresh() {
    // reset search directly (avoid clearSearch()'s emit to prevent a duplicate fetch)
    this.tableControls?.searchForm.patchValue({ search: '' });
    this.search_keyword = '';
    this.searchForm.reset({ search: '' });

    // reset limit back to default
    this.size = 5;
    if (this.tableControls) {
      this.tableControls.limitForm.patchValue({ limit: this.size });
    }

    // reset sort back to default
    this.sort_column = 'dateTime';
    this.sort_type = 'ASC';

    // reset pagination
    this.current_page = 1;

    // reset filterFields
    this.filterFields = {
      search: null,
    };

    // single refetch with reset params
    await this.get_ads_list();
  }

  disable(uid: number) {
    this.adsService.disable(uid, {
      itemList: this.ads_list,
      itemName: 'ad',
      titleField: 'title',
      statusUpdateMethod: (uid, status) => this.apiService.setStatusAds(uid, status),
      onSuccess: async () => {
        await this.get_ads_list();
      },
    });
  }

  enable(uid: number) {
    this.adsService.enable(uid, {
      itemList: this.ads_list,
      itemName: 'ad',
      titleField: 'title',
      statusUpdateMethod: (uid, status) => this.apiService.setStatusAds(uid, status),
      onSuccess: async () => {
        await this.get_ads_list();
      },
    });
  }

  async print() {
    this.spinner.show().catch((err) => {
      console.error('Spinner show error:', err);
    });

    const current = new Date();
    this.fileName = current.toLocaleDateString() + `-ads-dashboard-${this.activeAdType}.xlsx`;

    try {
      const report = await firstValueFrom(
        this.apiService.exportAds(this.filterFields)
      );

      const blob = new Blob([report], { type: 'application/vnd.ms-excel' });
      const data = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = data;
      link.download = this.fileName;
      link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));

      setTimeout(() => {
        this.spinner.hide().catch((err) => {
          console.error('Spinner hide error:', err);
        });
      }, 500);
    } catch (error) {
      this.toastr.error('Failed to export', 'Error!');
      this.spinner.hide().catch((err) => {
        console.error('Spinner hide error:', err);
      });
    }
  }
}
